var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useCallback, useMemo } from "react";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ResultsSelectorTabs as SharedResultsSelectorTabs } from "@sequentech/ui-essentials";
import { translatedLabel } from "@/services/resultLabels";
import { orderResultContestIds, orderResultElectionIds } from "@/services/resultsOrdering";
import { ContestResultsBlock } from "./ContestResultsBlock";
var sameId = function (left, right) {
    return left !== null &&
        left !== undefined &&
        right !== null &&
        right !== undefined &&
        String(left) === String(right);
};
var unique = function (values) {
    var seen = new Set();
    var output = [];
    values.forEach(function (value) {
        if (!value)
            return;
        var id = String(value);
        if (seen.has(id))
            return;
        seen.add(id);
        output.push(id);
    });
    return output;
};
var findRow = function (rows, id) {
    return id ? rows.find(function (row) { return sameId(row.id, id); }) : undefined;
};
export var ResultsSelectorTabs = function (_a) {
    var manifest = _a.manifest, dataset = _a.dataset, locale = _a.locale, initialElectionId = _a.initialElectionId, onElectionChange = _a.onElectionChange;
    var t = useTranslation().t;
    var electionIds = useMemo(function () { return orderResultElectionIds(manifest, dataset, locale); }, [dataset, locale, manifest]);
    var electionOptions = useMemo(function () {
        return electionIds.map(function (electionId) { return ({
            id: electionId,
            label: translatedLabel(findRow(dataset.election, electionId), locale, t("resultsPortal.fallbackElectionName")),
        }); });
    }, [dataset.election, electionIds, locale, t]);
    var getContestOptions = useCallback(function (selection) {
        var _a, _b;
        var selectedElectionId = (_b = (_a = selection.election) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null;
        var contestIds = orderResultContestIds(manifest, dataset, selectedElectionId, locale);
        return contestIds.map(function (contestId) { return ({
            id: contestId,
            label: translatedLabel(findRow(dataset.contest, contestId), locale, t("resultsPortal.fallbackContestName", { contestId: contestId })),
        }); });
    }, [dataset, locale, manifest, t]);
    var getAreaOptions = useCallback(function (selection) {
        var _a, _b, _c, _d;
        var selectedElectionId = (_b = (_a = selection.election) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null;
        var selectedContestId = (_d = (_c = selection.contest) === null || _c === void 0 ? void 0 : _c.id) !== null && _d !== void 0 ? _d : null;
        var contestManifests = manifest.contests.filter(function (contest) {
            return sameId(contest.election_id, selectedElectionId);
        });
        var areaIds = unique(__spreadArray(__spreadArray([], contestManifests
            .filter(function (contest) { return sameId(contest.contest_id, selectedContestId); })
            .map(function (contest) { return contest.area_id; }), true), dataset.results_area_contest
            .filter(function (result) {
            return sameId(result.election_id, selectedElectionId) &&
                sameId(result.contest_id, selectedContestId);
        })
            .map(function (result) { return result.area_id; }), true));
        var areaOptions = areaIds.map(function (areaId) { return ({
            id: areaId,
            label: translatedLabel(findRow(dataset.area, areaId), locale, areaId),
        }); });
        if (manifest.visibility_scope === "area_based") {
            return areaOptions;
        }
        return __spreadArray([
            {
                id: null,
                label: t("resultsPortal.globalArea"),
            }
        ], areaOptions, true);
    }, [dataset.area, dataset.results_area_contest, locale, manifest, t]);
    var selectedManifestContest = useCallback(function (selection) {
        var _a, _b, _c, _d, _e, _f, _g;
        var selectedElectionId = (_b = (_a = selection.election) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null;
        var selectedContestId = (_d = (_c = selection.contest) === null || _c === void 0 ? void 0 : _c.id) !== null && _d !== void 0 ? _d : null;
        var selectedAreaId = (_f = (_e = selection.area) === null || _e === void 0 ? void 0 : _e.id) !== null && _f !== void 0 ? _f : null;
        var contestManifests = manifest.contests.filter(function (contest) {
            return sameId(contest.election_id, selectedElectionId);
        });
        if (manifest.visibility_scope === "area_based" && !selectedAreaId) {
            return null;
        }
        var selectedGlobalManifestContest = (_g = contestManifests.find(function (contest) { return sameId(contest.contest_id, selectedContestId) && !contest.area_id; })) !== null && _g !== void 0 ? _g : contestManifests.find(function (contest) { return sameId(contest.contest_id, selectedContestId); });
        if (!selectedGlobalManifestContest) {
            return null;
        }
        if (!selectedAreaId) {
            return selectedGlobalManifestContest;
        }
        var manifestContest = contestManifests.find(function (contest) {
            return sameId(contest.contest_id, selectedContestId) &&
                sameId(contest.area_id, selectedAreaId);
        });
        if (manifestContest) {
            return manifestContest;
        }
        var hasAreaResults = dataset.results_area_contest.some(function (result) {
            return sameId(result.election_id, selectedElectionId) &&
                sameId(result.contest_id, selectedContestId) &&
                sameId(result.area_id, selectedAreaId);
        });
        return __assign(__assign({}, selectedGlobalManifestContest), { area_id: selectedAreaId, publication_state: selectedGlobalManifestContest.publication_state === "published" &&
                hasAreaResults
                ? "published"
                : "not_published" });
    }, [dataset.results_area_contest, manifest.contests]);
    var handleSelectionChange = useCallback(function (selection) { var _a; return onElectionChange === null || onElectionChange === void 0 ? void 0 : onElectionChange((_a = selection.electionId) !== null && _a !== void 0 ? _a : undefined); }, [onElectionChange]);
    return (_jsx(SharedResultsSelectorTabs, { className: "seq-results-portal-results-selector", labels: {
            elections: t("resultsPortal.electionsTitle"),
            contests: t("resultsPortal.contestsTitle"),
            areas: t("resultsPortal.areasTitle"),
            empty: t("resultsPortal.noResultsForSelection"),
        }, elections: electionOptions, initialElectionId: initialElectionId, getContests: getContestOptions, getAreas: getAreaOptions, onSelectionChange: handleSelectionChange, renderResult: function (selection) {
            var manifestContest = selectedManifestContest(selection);
            return manifestContest ? (_jsx(ContestResultsBlock, { manifestContest: manifestContest, dataset: dataset, locale: locale })) : (_jsx(Typography, { className: "seq-results-selector__empty", color: "text.secondary", sx: { mt: 3 }, children: t("resultsPortal.noResultsForSelection") }));
        } }));
};
